/**
 * FloatingUtilities — palette pill, responsively placed:
 *   • Mobile  → bottom-CENTER (out of the way of right-edge buttons)
 *   • Desktop → bottom-right, ICON-ONLY (a discreet circular palette),
 *               sat ~5rem in from the right edge so it doesn't overlap
 *               the crossed-swords FloatingHomeBtn that lives at the
 *               same bottom-right corner. Owen Jun 2026 round 34 fix
 *               for the laptop "palette and home button stacking on
 *               top of each other" report.
 *
 * The dropdown lives in ThemePicker; it opens UPWARD and is now capped
 * with max-h-[80vh] + overflow so it can never clip off the top of a
 * laptop viewport (Owen Jun 2026 round 9 fix).
 *
 * Wrapped in .theme-fixed so the chrome stays charcoal+gold on every
 * theme regardless of the user's current palette choice.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ThemePicker from '@/components/ThemePicker';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';

// Owen Jun 2026 round 21 — Palette must appear on every page EXCEPT the
// public tribute cinematic (`/tribute/<token>`), where the bottom-center
// Spotify pill needs the entire bottom strip. Without this carve-out the
// crossed-swords palette button physically overlaps the Spotify play
// button on mobile, blocking the tap. Also still hidden on raw auth
// flows + the founder /tour.
// Owen Feb 2026 round 37 — also hide on the public Landing page (`/`).
// Logged-out visitors don't need a theme switcher before they're in the
// product, and Owen wanted that bottom-strip clean on mobile.
// Owen Feb 28, 2026 round 63 — `/tour-builder` is a NEW protected page
// that does need the palette (Owen explicitly asked for it back).
// Tightened the `/tour` prefix to `/tour/` so `/tour-builder` no longer
// gets caught by the broad startsWith().
const HIDDEN_ROUTES = ['/auth/callback', '/auth/google', '/login', '/tour/', '/tribute/'];
const HIDDEN_EXACT = ['/', '/tour'];

const FloatingUtilities = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Owen Feb 28, 2026 round 55 — read signed-in state from AuthContext
  // instead of polling localStorage on every route change. The localStorage
  // marker can lag behind the real session (e.g. just after logout the
  // value is cleared but a fresh-mounted component still saw it), causing
  // the logout pill to flicker. AuthContext is the single source of truth.
  const { isAuthenticated, signOut } = useAuth();

  // Owen Feb 28, 2026 round 64 — auto-hide the bottom pill strip while
  // a global modal/dialog is open. Owen reported the "Bring N people
  // across" CTA bar inside SelectiveMirrorModal was getting overlapped
  // by the floating palette/language/sword pills. Modals declare
  // themselves either via `role="dialog"` (Radix) or via the convention
  // `[data-testid$="-modal"]` (our hand-rolled overlays). When at least
  // one is mounted, hide the strip so the modal's action bar can own
  // the bottom of the screen.
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      // A dialog that's visible (not display:none) and either Radix-style
      // role=dialog or one of our overlay test-ids.
      const candidates = document.querySelectorAll(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-testid$="-modal"], [data-testid="selective-mirror-modal"]'
      );
      setModalOpen(candidates.length > 0);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, []);

  if (HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p))) return null;
  if (HIDDEN_EXACT.includes(location.pathname)) return null;
  if (modalOpen) return null;

  const handleLogout = async () => {
    await signOut();
    toast.success('Signed out.');
    navigate('/');
  };

  return (
    <>
      {/* Owen Jul 1, 2026 — bottom strip re-balanced by Owen's brief:
             LEFT   → Language pill (wide element balances the pair on the right)
             RIGHT  → [Palette] [Swords back]  ← grouped, with the swords
                       button as the outermost right edge (rendered by
                       FloatingHomeBtn.js) and the palette sitting
                       immediately inward of it. This leaves an
                       intentional visual gap in the middle so the
                       cluster reads as two balanced sides rather than
                       three centred items. All three sit on the same
                       y-axis at the same 40px height. */}

      {/* Palette — bottom-CENTER of the viewport. Owen Feb 2026 (later
          request): move the palette back to the middle of the bottom
          strip, where it originally sat before the right-cluster
          re-balance. Language pill still sits on the left, swords back
          button on the right, palette centred between them.
          left-1/2 + -translate-x-1/2 handles the horizontal centring in
          both mobile and desktop. Same 40px pill height keeps the
          three-item horizontal baseline aligned. */}
      <div
        className="theme-fixed fixed z-[60] left-1/2 -translate-x-1/2
                   bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)]
                   sm:bottom-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]"
        data-testid="floating-utilities-cluster"
      >
        <ThemePicker
          floatingDirection="up"
          desktopIconOnly
          onLogout={isAuthenticated ? handleLogout : null}
        />
      </div>

      {/* Language — left edge. */}
      <div
        className="theme-fixed fixed z-[60]
                   bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] left-4
                   sm:bottom-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] sm:left-[calc(env(safe-area-inset-left,0px)+1.25rem)]"
        data-testid="floating-language-pill"
      >
        <LanguageSwitcher dropUp />
      </div>
    </>
  );
};

export default FloatingUtilities;
